import OpenAI from 'openai';
import { analyzeDocumentsWithAssistant } from './openaiAssistantsService';
import { SYSTEM_PROMPTS } from '../config/systemPrompts.js';

// Initialize OpenAI client
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

/**
 * Error message mapping for OpenAI API errors
 */
function getErrorMessage(error) {
  if (error instanceof OpenAI?.AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof OpenAI?.PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof OpenAI?.RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof OpenAI?.InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof OpenAI?.APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Entrepreneur Tool System Prompt
 */
const SYSTEM_PROMPT = SYSTEM_PROMPTS?.['entrepreneur_tool'];

/**
 * Stream entrepreneur tool chat response using gpt-4o (primary model)
 * @param {Array} messages - Conversation messages
 * @param {Function} onChunk - Callback for streaming chunks
 * @param {Function} onProgress - Callback for progress updates
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @returns {Promise<{content: string, model: string}>} Complete response with metadata
 */
export async function streamEntrepreneurToolResponse(
  messages,
  onChunk,
  onProgress,
  signal = null
) {
  const PRIMARY_MODEL = 'gpt-4o';
  
  let fullResponse = '';

  // Entrepreneur Tool System Prompt - use the centralized constant

  try {
    onProgress?.({
      status: 'Gerando resposta sobre empreendedorismo...',
      model: PRIMARY_MODEL
    });

    // Inject system prompt as first message
    const messagesWithSystem = [
      { role: 'system', content: SYSTEM_PROMPT },
      ...messages
    ];

    const stream = await openai?.chat?.completions?.create({
      model: PRIMARY_MODEL,
      messages: messagesWithSystem,
      stream: true,
      max_tokens: 4096,
      temperature: 0.7,
    }, { signal });

    for await (const chunk of stream) {
      if (signal?.aborted) {
        throw new Error('Geração cancelada pelo usuário');
      }

      const content = chunk?.choices?.[0]?.delta?.content || '';
      
      if (content) {
        fullResponse += content;
        onChunk?.(content);
      }
    }

    onProgress?.({
      status: 'Resposta completa',
      model: PRIMARY_MODEL
    });

    return {
      content: fullResponse,
      model: PRIMARY_MODEL
    };

  } catch (error) {
    if (error?.message === 'Geração cancelada pelo usuário') {
      throw error;
    }
    
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in entrepreneur tool chat streaming:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Process document files using hybrid approach:
 * - gpt-4o-mini for reading and indexing content
 * - gpt-4o for generating final response using indexed data
 * @param {Array} files - Array of File objects to analyze
 * @param {string} prompt - User's analysis prompt
 * @param {string} conversationId - Current conversation ID
 * @param {Function} onChunk - Callback for streaming chunks
 * @param {Function} onProgress - Callback for progress updates
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @param {number} maxWaitTime - Maximum wait time for analysis
 * @returns {Promise<{content: string, model: string, tokensUsed: number}>} Analysis result
 */
export async function processEntrepreneurToolDocuments(
  files,
  prompt,
  conversationId,
  onChunk,
  onProgress,
  signal = null,
  maxWaitTime = 60000
) {
  const INDEXING_MODEL = 'gpt-4o-mini';
  const RESPONSE_MODEL = 'gpt-4o';

  // Get system prompt from centralized configuration
  // For document analysis, we use the same entrepreneur_tool prompt
  // as it already contains document analysis instructions
  const systemPrompt = SYSTEM_PROMPT;

  try {
    // STEP 1: Use gpt-4o-mini for document reading and indexing
    onProgress?.({
      status: 'Lendo e indexando documentos (gpt-4o-mini)...',
      model: INDEXING_MODEL,
      step: 1,
      totalSteps: 2
    });

    const indexingResult = await analyzeDocumentsWithAssistant(
      files,
      `Analise os documentos fornecidos e extraia todas as informações relevantes sobre empreendedorismo e negócios. Identifique conceitos-chave, estratégias, dados importantes, recomendações e insights. Organize as informações de forma estruturada para facilitar a compreensão.\n\nPergunta do usuário: ${prompt}`,
      conversationId,
      null,
      (progress) => {
        onProgress?.({
          ...progress,
          model: INDEXING_MODEL,
          step: 1,
          totalSteps: 2
        });
      },
      signal,
      maxWaitTime,
      INDEXING_MODEL
    );

    if (signal?.aborted) {
      throw new Error('Análise cancelada pelo usuário');
    }

    // STEP 2: Use gpt-4o to generate final response using indexed data
    onProgress?.({
      status: 'Gerando resposta final (gpt-4o)...',
      model: RESPONSE_MODEL,
      step: 2,
      totalSteps: 2
    });

    const messages = [
      { role: 'system', content: systemPrompt },
      { 
        role: 'user', 
        content: `Baseado na análise dos documentos fornecidos, responda à seguinte pergunta de forma completa e empática:\n\n${prompt}\n\nInformações extraídas dos documentos:\n${indexingResult?.content}` 
      }
    ];

    let fullResponse = '';

    const stream = await openai?.chat?.completions?.create({
      model: RESPONSE_MODEL,
      messages: messages,
      stream: true,
      max_tokens: 4096,
      temperature: 0.7,
    }, { signal });

    for await (const chunk of stream) {
      if (signal?.aborted) {
        throw new Error('Geração cancelada pelo usuário');
      }

      const content = chunk?.choices?.[0]?.delta?.content || '';
      
      if (content) {
        fullResponse += content;
        onChunk?.(content);
      }
    }

    onProgress?.({
      status: 'Análise completa',
      model: `${INDEXING_MODEL} → ${RESPONSE_MODEL}`,
      step: 2,
      totalSteps: 2
    });

    return {
      content: fullResponse,
      model: `${INDEXING_MODEL} (indexação) → ${RESPONSE_MODEL} (resposta)`,
      tokensUsed: (indexingResult?.tokensUsed || 0)
    };

  } catch (error) {
    if (error?.message === 'Análise cancelada pelo usuário' || error?.message === 'Geração cancelada pelo usuário') {
      throw error;
    }
    
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in entrepreneur tool document processing:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Check if OpenAI API is properly configured and accessible
 * @returns {Promise<{connected: boolean, model: string}>} Connection status
 */
export async function checkEntrepreneurToolConnection() {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [{ role: 'user', content: 'test' }],
      max_tokens: 5,
    });

    return {
      connected: true,
      model: 'gpt-4o'
    };
  } catch (error) {
    console.error('Entrepreneur Tool API connection check failed:', error);
    return {
      connected: false,
      model: null
    };
  }
}
